import { mappedColors } from '@pixee/design-system'
import { type FindingOverlayTier } from '../../utils/api-client/api-schemas'
import { pluralize } from '../../utils/text-utils'
import { TooltipWithHover } from '../tooltip'
import { SegmentsBar, type SegmentProps, type SegmentsBarSize } from './segments-bar'
import { SegmentsLabels, type SegmentLabelProps } from './segments-labels'

export type PrioritySegment = 'Critical' | 'High' | 'Medium' | 'Low' | 'Unknown'

/**
 * Severity tiers keep the app's severity ramp rather than the tool-hued alpha ramp the triage/fix/SCA
 * bars use. Those bars segment one tool's findings by outcome, so a single hue at descending opacity
 * reads correctly; this bar segments by severity, where "Critical" already has a colour everywhere else
 * on the page — the tier badge on each card and the score tag both use it. Borrowing a tool hue here
 * would mean the same word wore two colours a few hundred pixels apart.
 */
const TIER_COLORS = {
  critical: mappedColors.severity.criticalBackground,
  high: mappedColors.severity.highBackground,
  medium: mappedColors.severity.mediumBackground,
  low: mappedColors.severity.lowBackground,
  unknown: mappedColors.severity.unknownBackground,
} as const satisfies Record<FindingOverlayTier, string>

const TIER_SEGMENT_LABELS = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  unknown: 'Unknown',
} as const satisfies Record<FindingOverlayTier, PrioritySegment>

// The indeterminate tier sits at the tail of the ramp: it is not a severity, so it does not belong
// between two that are.
const TIER_ORDER = ['critical', 'high', 'medium', 'low', 'unknown'] as const satisfies readonly FindingOverlayTier[]

export type PriorityCompositionBarProps = {
  counts: Record<FindingOverlayTier, number>
  size?: SegmentsBarSize
  /** Tiers the reader has narrowed to. Empty means no filter, so nothing dims. */
  selectedTiers?: readonly FindingOverlayTier[]
  /** Toggles a tier in the filter. Omit to render the bar as a read-only distribution. */
  onSegmentClick?: (tier: FindingOverlayTier) => unknown
}

export const PriorityCompositionBar: React.FC<PriorityCompositionBarProps> = ({
  counts,
  // `tiny` is what the scan details header renders, and this bar sits in the same reading position on
  // its own page — the thicker `small` made it read as a progress bar rather than a distribution.
  size = 'tiny',
  selectedTiers = [],
  onSegmentClick,
}) => {
  const total = TIER_ORDER.reduce((sum, tier) => sum + counts[tier], 0)
  const hasActiveFilters = selectedTiers.length > 0

  // The four severities are a fixed vocabulary, so a zero among them is worth seeing — "no criticals"
  // is a fact about the repository. `unknown` is not part of that vocabulary: it is what the backend
  // sends when it could not tier a finding, so at zero it is an artifact rather than a fact. It has to
  // be excluded explicitly, because `SegmentsBar` deliberately draws every zero as a 1% sliver and
  // `SegmentsLabels` gives every entry a tooltip row.
  const shownTiers = TIER_ORDER.filter(tier => tier !== 'unknown' || counts.unknown > 0)

  const segments: SegmentProps<PrioritySegment>[] = shownTiers.map(tier => ({
    ariaLabel: TIER_SEGMENT_LABELS[tier],
    percentage: total > 0 ? (counts[tier] / total) * 100 : 0,
    color: TIER_COLORS[tier],
    pressed: selectedTiers.includes(tier),
    dimmed: hasActiveFilters && !selectedTiers.includes(tier),
    // A tier with nothing in it is a 1% sliver the reader cannot aim at and which would filter the
    // list down to nothing, so it stays inert while still holding its place in the ramp.
    onClick: onSegmentClick && counts[tier] > 0 ? () => onSegmentClick(tier) : undefined,
  }))

  const labels: SegmentLabelProps<PrioritySegment>[] = shownTiers.map(tier => ({
    name: TIER_SEGMENT_LABELS[tier],
    percentage: total > 0 ? (counts[tier] / total) * 100 : 0,
    subLabel: `${counts[tier]} ${pluralize(counts[tier], 'finding', 'findings')}`,
    color: TIER_COLORS[tier],
  }))

  return (
    <TooltipWithHover
      trigger={<SegmentsBar segments={segments} size={size} />}
      maxWidth={1200}
      side="bottom"
      sideOffset={8}
      bare
    >
      <SegmentsLabels labels={labels} />
    </TooltipWithHover>
  )
}
